import { Username } from "@moneta/core/browser";
import { useNavigation } from "@remix-run/react";
import { EllipsisVerticalIcon, ExternalLinkIcon, HandCoinsIcon, LogOutIcon } from "lucide-react";
import { ReactNode } from "react";
import { match } from "ts-pattern";
import { useLogout } from "~/hooks";
import { usePaywall } from "~/hooks/usePaywall";
import { cn, trpc, useRootLoaderData } from "~/utils";
import { IndeterminateProgress } from "./IndeterminateProgress";
import { MonetaStudioBrand } from "./MonetaStudioBrand";
import { ThemeMenuRadioItems } from "./ThemeMenu";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui";

function UserMenu({ username, dropdownItems }: { username: Username; dropdownItems: ReactNode }) {
  const { data: isPro, isLoading: isProLoading } = trpc.user.isPro.useQuery();
  const { showPaywall } = usePaywall();
  const logout = useLogout();

  return (
    <div className="flex items-center gap-1">
      <span className="text-primaryText text-xl tracking-tight">{username}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button icon type="button" variant="inverted" size="small" data-testid="user-menu">
            <EllipsisVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-[240px]">
          {dropdownItems}
          {match({ isPro, isProLoading })
            .with({ isProLoading: true }, () => null)
            .with({ isPro: true }, () => (
              <>
                <DropdownMenuLabel>Pro subscription is active</DropdownMenuLabel>
                <DropdownMenuItem onSelect={() => showPaywall()}>
                  <HandCoinsIcon className="mr-2 h-4 w-4" />
                  Buy more messages
                </DropdownMenuItem>
              </>
            ))
            .otherwise(() => (
              <DropdownMenuItem onSelect={() => showPaywall()}>
                <ExternalLinkIcon className="mr-2 h-4 w-4" />
                Upgrade to Pro
              </DropdownMenuItem>
            ))}
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Theme</DropdownMenuLabel>
          <ThemeMenuRadioItems />
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => logout()}>
            <LogOutIcon className="mr-2 h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export const DefaultNav = ({
  homeUrlOverride,
  onLogoClick,
  className,
  userMenu,
  rightContent,
}: {
  homeUrlOverride?: string;
  onLogoClick?: () => void;
  className?: string;
  userMenu?: ReactNode;
  rightContent?: ReactNode;
}) => {
  const navigation = useNavigation();
  const { user, baseHomeUrl, release } = useRootLoaderData();

  const homeUrl = homeUrlOverride ?? baseHomeUrl;
  const showLoader = navigation.state === "loading" || navigation.state === "submitting";

  return (
    <>
      {showLoader && (
        <div className="fixed top-0 w-full">
          <IndeterminateProgress />
        </div>
      )}
      <nav
        className={cn(
          "h-header flex shrink-0 items-center justify-between gap-4 px-2",
          // ensure that e.g. 404 hero doesn't cover the logo and link interactions
          "z-50",
          className,
        )}
      >
        <MonetaStudioBrand
          to={homeUrl}
          release={release}
          onClick={(e) => {
            if (onLogoClick) {
              e.preventDefault();
              onLogoClick();
            }
          }}
        />
        {user && userMenu ? (
          <UserMenu username={user.username} dropdownItems={userMenu} />
        ) : (
          rightContent && <div className="flex items-center">{rightContent}</div>
        )}
      </nav>
    </>
  );
};

export const DefaultLayout = ({ children, homeUrl: homeUrlOverride }: { children: ReactNode; homeUrl?: string }) => {
  return (
    <div className="flex h-full flex-col">
      <DefaultNav homeUrlOverride={homeUrlOverride} />
      {children}
    </div>
  );
};
